import { useCallback, useSyncExternalStore } from "react";

// Remplace `useWindowDimensions` / `Dimensions` de React Native.
//
// On s'abonne à MatchMedia plutôt qu'à `resize` : le navigateur ne notifie
// qu'au franchissement du seuil, là où `resize` déclenche à chaque pixel.
//
// useSyncExternalStore garantit un état cohérent au premier rendu (pas de flash
// mobile→desktop) et reste correct en mode concurrent.

export function useMediaQuery(query: string): boolean {
  // useCallback est nécessaire : useSyncExternalStore se réabonne à chaque
  // changement d'identité de `subscribe`.
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  // Rendu serveur / environnement de test sans matchMedia : on retombe sur
  // mobile, qui est le design de référence.
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// Seuils alignés sur Tailwind et sur §6 de MIGRATION_WEB.md.
export const useIsTabletUp = () => useMediaQuery("(min-width: 768px)"); // md
export const useIsDesktopUp = () => useMediaQuery("(min-width: 1024px)"); // lg
export const useIsWideUp = () => useMediaQuery("(min-width: 1280px)"); // xl

/**
 * Écran COURT — un seuil de HAUTEUR, pas de largeur.
 *
 * Les écrans qui empilent un titre, un montant géant, des raccourcis, un pavé
 * numérique et un bouton d'action (recharge, retrait) tiennent tout juste sur
 * un téléphone. Sous ce seuil, le conteneur central se fait écraser par ses
 * voisins et le montant déborde sur le texte au-dessus — c'est de la place
 * verticale qui manque, or les seuils `md`/`lg` ne parlent que de largeur : un
 * téléphone en paysage est large ET court.
 *
 * 780px correspond au format des appareils courants ici (720–800 de haut).
 */
export const useIsShortViewport = () => useMediaQuery("(max-height: 780px)");
